import {
  lazy,
  Suspense,
  useState,
  startTransition,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { Link, useLocation, useNavigate, useRevalidator } from "@remix-run/react";
import {
  Search,
  Menu,
  Heart,
  CircleUserRound,
  ChevronDown,
} from "lucide-react";
import { Button } from "~/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "~/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { getNavigationItems } from "./constants";
import IconButton from "~/lib/utils/Buttons/IconButton";
import {
  useCurrentCurrency,
  useCurrentRegion,
} from "~/lib/helpers/CurrencyHelper";
import { DEFAULT_COUNTRY, DEFAULT_REGION } from "~/lib/constants/generalConstants";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTrigger,
} from "~/components/ui/dialog";
import EnquireNowForm from "../common/EnquireNowForm";
import { useFavorite } from "~/lib/hooks/useFavourite";
import { getRegionUrlPrefixWithCountryCode } from "~/lib/helpers/slugHelper";
import { getRegionAwareSearchUrl } from "~/lib/helpers/ipLocationSearchPathHelper";
import { navigateOrRevalidateLocationSearch } from "~/components/blocks/Geosearch/locationGeosearch";
import { OhButton } from "~/components/ui/custom-radix-components/button";
import Container from "~/components/atoms/Container";
import { getFlagUrl } from "custom-config/region.configs";
import { cn } from "~/lib/utils";
import { EmptyLazyFallback, loadLazyModule } from "~/lib/helpers/resolveLazyModule";

// Lazy loaded components
const Navigation = lazy(() => loadLazyModule(() => import("~/components/blocks/Mobile/Navigation"), EmptyLazyFallback));
const AutoPlaces = lazy(() => loadLazyModule(() => import("../Geosearch/AutoPlaces"), EmptyLazyFallback));
const GlobalSettings = lazy(
  () => loadLazyModule(() => import("~/components/blocks/Header/NavItems/GlobalSettings"), EmptyLazyFallback),
);
const LoginOptionCard = lazy(
  () => loadLazyModule(() => import("~/components/blocks/Header/NavItems/LoginOptionCard"), EmptyLazyFallback),
);
const UniqueVisitorModal = lazy(() =>
  loadLazyModule(
    () =>
      import("../Modals/UniqueVisitorModal/UniqueVisitorModal").then((mod) =>
        mod?.UniqueVisitorModal ? { default: mod.UniqueVisitorModal } : undefined,
      ),
    EmptyLazyFallback,
  ),
);
const AddListingTwoStepModal = lazy(() =>
  loadLazyModule(() => import("../Modals/AddListingTwoStepModal"), EmptyLazyFallback),
);
import GlobalPhone from "./NavItems/GlobalPhone";

// Single source of truth for the nav motion. Short, delay-free, and only
// opacity/transform animate so the compositor handles every frame.
const NAV_TRANSITION = "opacity 200ms ease, transform 200ms ease";
const NAV_HOVER_INTENT_MS = 200;

export default function Header({
  isMobile,
  region = DEFAULT_REGION,
  config,
  location: ipLocation,
  mobileSheetOpen: controlledSheetOpen,
  setMobileSheetOpen: setControlledSheetOpen,
}: {
  isMobile: any;
  region?: string;
  config?: any;
  location?: any;
  mobileSheetOpen?: boolean;
  setMobileSheetOpen?: (open: boolean) => void;
}) {
  const location = useLocation();
  let { pathname } = location;

  const pathNoTrailingSlash = pathname.replace(/\/+$/, "") || "/";
  const homePath = region === "us" ? "/" : `/${region}`;
  const addListingPath =
    `${getRegionUrlPrefixWithCountryCode(region)}/add-listing`.replace(
      /\/+/g,
      "/",
    );
  const showAddListingHeaderCta =
    pathNoTrailingSlash === homePath || pathNoTrailingSlash === addListingPath;
  const [navValue, setNavValue] = useState<string | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [open, setopen] = useState(false);
  const storeConfig = useCurrentRegion();
  const regionConfig = config || storeConfig;
  const displayCurrency = useCurrentCurrency() || regionConfig?.currency;
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  const { favoritesCount } = useFavorite();

  const resolvedFindSearchUrl = useMemo(
    () => getRegionAwareSearchUrl(region, ipLocation),
    [region, ipLocation],
  );

  const handleFindNavigate = useCallback(() => {
    navigate(resolvedFindSearchUrl);
  }, [navigate, resolvedFindSearchUrl]);
  const [addListingModal, setAddListingModal] = useState(false);
  const [addListingInitialStep, setAddListingInitialStep] = useState<1 | 2>(1);
  const [addListingInitialEmail, setAddListingInitialEmail] = useState("");
  const [uniqueVisitorModalOpen, setUniqueVisitorModalOpen] = useState(false);
  const [sheetViewportHeight, setSheetViewportHeight] = useState<number | null>(
    null,
  );
  const [sheetViewportTop, setSheetViewportTop] = useState(0);
  const [sheetKeyboardInset, setSheetKeyboardInset] = useState(0);
  const [internalSheetOpen, setInternalSheetOpen] = useState(false);
  const isSheetOpen =
    controlledSheetOpen !== undefined ? controlledSheetOpen : internalSheetOpen;
  const setIsSheetOpen = setControlledSheetOpen ?? setInternalSheetOpen;
  const dropdownTriggerRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const dropdownContentRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const dropdownHoverTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const [dropdownAnchorLeftOffset, setDropdownAnchorLeftOffset] = useState<
    Record<string, number>
  >({});

  const closeNavDropdown = useCallback(() => {
    setNavValue(null);
  }, []);

  const openUniqueVisitorModal = useCallback(() => {
    setNavValue(null);
    setUniqueVisitorModalOpen(true);
  }, []);

  const openAddListingModal = useCallback(
    (options?: { step?: 1 | 2; email?: string }) => {
      setAddListingInitialStep(options?.step ?? 1);
      setAddListingInitialEmail(options?.email?.trim() ?? "");
      setAddListingModal(true);
    },
    [],
  );

  const closeAddListingModal = useCallback(() => {
    setAddListingModal(false);
    setAddListingInitialStep(1);
    setAddListingInitialEmail("");
  }, []);

  const openAddListingFromPartners = useCallback(() => {
    setNavValue(null);
    openAddListingModal();
  }, [openAddListingModal]);

  const setAddListingModalOpen = useCallback(
    (open: boolean) => {
      if (open) {
        openAddListingModal();
      } else {
        closeAddListingModal();
      }
    },
    [openAddListingModal, closeAddListingModal],
  );

  const navigationItems = useMemo(
    () =>
      getNavigationItems({
        onSpeakToFlexpert: openUniqueVisitorModal,
        onOpenAddListing: openAddListingFromPartners,
        onFindNavigate: handleFindNavigate,
        findSearchUrl: resolvedFindSearchUrl,
      }),
    [
      openUniqueVisitorModal,
      openAddListingFromPartners,
      handleFindNavigate,
      resolvedFindSearchUrl,
    ],
  );

  const firstDropdownNavLabel =
    navigationItems.find((item) => item.hasDropdown)?.label ?? null;

  // Each dropdown lives inside its own trigger, so the only thing we measure
  // is a horizontal offset that aligns every panel to the first tab. No
  // top/left is computed on open, which is what keeps the animation jank-free.
  const updateDropdownPosition = useCallback(
    (label: string) => {
      if (typeof window === "undefined" || !firstDropdownNavLabel) {
        return;
      }

      const anchorTrigger = dropdownTriggerRefs.current[firstDropdownNavLabel];
      const currentTrigger = dropdownTriggerRefs.current[label];

      if (!anchorTrigger || !currentTrigger) {
        return;
      }

      const anchorRect = anchorTrigger.getBoundingClientRect();
      const currentRect = currentTrigger.getBoundingClientRect();
      const leftOffset = anchorRect.left - currentRect.left;

      setDropdownAnchorLeftOffset((previous) => {
        if (previous[label] === leftOffset) {
          return previous;
        }

        return {
          ...previous,
          [label]: leftOffset,
        };
      });
    },
    [firstDropdownNavLabel],
  );

  useEffect(() => {
    setIsSheetOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    setNavValue(null);
  }, [location.pathname]);

  // Measure every dropdown once on mount (and on resize) so the first open
  // is already aligned and never shifts mid-transition.
  useEffect(() => {
    if (isMobile || typeof window === "undefined") {
      return;
    }

    const dropdownLabels = navigationItems
      .filter((item) => item.hasDropdown)
      .map((item) => item.label);

    let frame = 0;

    const measureAll = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        dropdownLabels.forEach((label) => updateDropdownPosition(label));
      });
    };

    measureAll();
    window.addEventListener("resize", measureAll);

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", measureAll);
    };
  }, [isMobile, navigationItems, updateDropdownPosition]);

  useEffect(() => {
    if (!isMobile) return;
    if (typeof window === "undefined") return;

    const updateViewport = () => {
      const viewport = window.visualViewport;
      const layoutHeight = window.innerHeight;
      const visualHeight = viewport?.height ?? layoutHeight;
      const offsetTop = viewport?.offsetTop ?? 0;
      const roundedOffsetTop = Math.round(offsetTop);

      setSheetViewportTop(roundedOffsetTop);
      setSheetViewportHeight(Math.round(layoutHeight - offsetTop));
      const keyboardInset = Math.max(
        0,
        Math.round(layoutHeight - (visualHeight + offsetTop)),
      );
      setSheetKeyboardInset(keyboardInset);
    };

    updateViewport();

    const viewport = window.visualViewport;
    viewport?.addEventListener("resize", updateViewport);
    viewport?.addEventListener("scroll", updateViewport);
    window.addEventListener("resize", updateViewport);

    return () => {
      viewport?.removeEventListener("resize", updateViewport);
      viewport?.removeEventListener("scroll", updateViewport);
      window.removeEventListener("resize", updateViewport);
    };
  }, [isMobile]);

  // Re-sync only the open dropdown, and only while it is actually open.
  useEffect(() => {
    if (!navValue || typeof window === "undefined") {
      return;
    }

    const syncDropdownPosition = () => updateDropdownPosition(navValue);
    const animationFrame = window.requestAnimationFrame(syncDropdownPosition);
    const dropdownElement = dropdownContentRefs.current[navValue];
    const resizeObserver =
      typeof ResizeObserver !== "undefined" && dropdownElement
        ? new ResizeObserver(syncDropdownPosition)
        : null;

    window.addEventListener("resize", syncDropdownPosition);
    if (resizeObserver && dropdownElement) {
      resizeObserver.observe(dropdownElement);
    }

    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.removeEventListener("resize", syncDropdownPosition);
      resizeObserver?.disconnect();
    };
  }, [navValue, updateDropdownPosition]);

  useEffect(
    () => () => {
      if (dropdownHoverTimeoutRef.current) {
        clearTimeout(dropdownHoverTimeoutRef.current);
      }
    },
    [],
  );

  const handleDropdownMouseEnter = useCallback(
    (label: string) => {
      if (dropdownHoverTimeoutRef.current) {
        clearTimeout(dropdownHoverTimeoutRef.current);
        dropdownHoverTimeoutRef.current = undefined;
      }

      // A menu is already open: intent is proven, so switch instantly.
      if (navValue !== null) {
        setNavValue(label);
        return;
      }

      // Nothing open yet: a short rest on the tab avoids opening menus the
      // cursor is merely passing over.
      dropdownHoverTimeoutRef.current = setTimeout(() => {
        dropdownHoverTimeoutRef.current = undefined;
        setNavValue(label);
      }, NAV_HOVER_INTENT_MS);
    },
    [navValue],
  );

  const handleDropdownMouseLeave = useCallback(() => {
    if (dropdownHoverTimeoutRef.current) {
      clearTimeout(dropdownHoverTimeoutRef.current);
      dropdownHoverTimeoutRef.current = undefined;
    }
    closeNavDropdown();
  }, [closeNavDropdown]);

  const isDropdownOpen = navValue !== null;

  return (
    <>
      <div
        className={cn(
          "nav-base:block fixed inset-0 z-20 hidden cursor-default bg-zinc-900/20 will-change-[opacity]",
          isDropdownOpen ? "opacity-100" : "pointer-events-none opacity-0",
        )}
        style={{ transition: "opacity 200ms ease" }}
        aria-hidden={!isDropdownOpen}
        onClick={closeNavDropdown}
      />
      <nav className='relative z-30 flex h-[70px] items-center justify-center px-[1rem] py-[0.5rem] bg-white border-b border-zinc-200'>
        <Container
          htmlFor='max-wide'
          className='flex w-full lg:p-0 lg:m-0 items-center justify-between'
        >
          <div className='flex items-center justify-between gap-4'>
            <div className='flex items-center gap-5'>
              <Link
                data-spinner='true'
                to={region === "us" ? "/" : `/${region}`}
                prefetch='intent'
                aria-label='Office Hub Home'
              >
                <img
                  src={`${process.env.PUBLIC_REMIX_APP_ASSESTS_IMAGES}/assets/office-hub.avif`}
                  alt='Office Hub Logo'
                  title='Office Hub'
                  width={175}
                  height={50}
                  fetchPriority="low"
                  decoding="async"
                  className='w-[140px] h-[40px] object-contain'
                />
              </Link>
            </div>

            <div
              className='nav-base:flex relative hidden'
              onMouseLeave={handleDropdownMouseLeave}
            >
              <div className='flex gap-0.5'>
                {navigationItems.map((item) => {
                  const isOpen = navValue === item.label;
                  const anchorLeft = dropdownAnchorLeftOffset[item.label];

                  if (item.hasDropdown) {
                    return (
                      <div
                        key={item.label}
                        className='relative'
                        ref={(node) => {
                          dropdownTriggerRefs.current[item.label] = node;
                        }}
                        onMouseEnter={() => handleDropdownMouseEnter(item.label)}
                      >
                        <div
                          className={cn(
                            "group relative inline-flex h-10 items-center justify-center rounded-md bg-background p-2 text-sm font-medium text-zinc-900 transition-colors duration-150 ease-out hover:bg-accent",
                            "after:pointer-events-none after:absolute after:bottom-1 after:left-2 after:right-2 after:h-[2px] after:origin-center after:scale-x-0 after:rounded-full after:bg-oh-primary after:transition-transform after:duration-200 after:ease-out group-hover:after:scale-x-100",
                            isOpen && "bg-accent after:scale-x-100",
                          )}
                        >
                          {item.onClick ? (
                            <span
                              role='link'
                              tabIndex={0}
                              className='cursor-pointer p-2'
                              data-spinner='true'
                              onClick={() => {
                                setNavValue(null);
                                item.onClick?.();
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  setNavValue(null);
                                  item.onClick?.();
                                }
                              }}
                            >
                              {item.label}
                            </span>
                          ) : item.url ? (
                            <Link
                              to={`${getRegionUrlPrefixWithCountryCode(region)}${item.url}`}
                              className='p-2'
                              data-spinner='true'
                              onClick={() => setNavValue(null)}
                            >
                              {item.label}
                            </Link>
                          ) : (
                            <span className='p-2'>{item.label}</span>
                          )}
                          <button
                            type='button'
                            className='ml-1 inline-flex items-center justify-center'
                            onClick={() =>
                              setNavValue((prev) =>
                                prev === item.label ? null : item.label,
                              )
                            }
                          >
                            <ChevronDown
                              size={12}
                              className={cn(
                                "transition-transform duration-200 ease-out",
                                isOpen && "rotate-180",
                              )}
                              strokeWidth={2}
                            />
                          </button>
                        </div>

                        <div
                          ref={(node) => {
                            dropdownContentRefs.current[item.label] = node;
                          }}
                          className='absolute left-0 top-full z-30 box-border -mt-1 min-w-0 w-max max-w-[95vw] overflow-visible pt-1 will-change-[opacity,transform]'
                          style={{
                            left: anchorLeft ?? 0,
                            opacity: isOpen ? 1 : 0,
                            transform: isOpen
                              ? "translateY(0)"
                              : "translateY(-8px)",
                            pointerEvents: isOpen ? "auto" : "none",
                            visibility: isOpen ? "visible" : "hidden",
                            transition: NAV_TRANSITION,
                          }}
                          aria-hidden={!isOpen}
                          onMouseEnter={() => setNavValue(item.label)}
                        >
                          <Suspense fallback={null}>
                            <div
                              onClick={(e) => {
                                const target = e.target as HTMLElement;
                                if (
                                  target.closest(
                                    "input, textarea, select, button, [contenteditable='true']",
                                  )
                                ) {
                                  return;
                                }
                                closeNavDropdown();
                              }}
                            >
                              {item.content}
                            </div>
                          </Suspense>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div key={item.label} className='relative'>
                      <Button
                        variant='ghost'
                        className='group relative text-sm p-2 text-zinc-900 transition-colors duration-150 ease-out after:pointer-events-none after:absolute after:bottom-1 after:left-2 after:right-2 after:h-[2px] after:origin-center after:scale-x-0 after:rounded-full after:bg-oh-primary after:transition-transform after:duration-200 after:ease-out hover:after:scale-x-100'
                        onClick={() => {
                          setNavValue(null);
                          navigate(
                            `${getRegionUrlPrefixWithCountryCode(region)}${item.url}`,
                          );
                        }}
                      >
                        {item.label}
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className='flex items-center justify-center gap-2 md:gap-4 pb-5'>
            <div className='flex items-center gap-2'>
              <div className='hidden nav-base:flex'>
                <DropdownMenu open={isMenuOpen} onOpenChange={setIsMenuOpen}>
                  <DropdownMenuTrigger asChild>
                    <IconButton
                      icon={<Search size={24} strokeWidth={1} />}
                      label='open search'
                      title='search'
                      size='md'
                    />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align='end'
                    className='-mt-12 min-w-[370px]'
                  >
                    <Suspense
                      fallback={
                        <div className='p-3 text-sm text-gray-500'>
                          Loading search…
                        </div>
                      }
                    >
                      <AutoPlaces
                        fixed
                        focus
                        value=""
                        onLocationSelect={(val: string) => {
                          startTransition(() => {
                            navigateOrRevalidateLocationSearch({
                              target: val,
                              currentPathname: location.pathname,
                              currentSearch: location.search,
                              navigate: (to) => navigate(to),
                              revalidate: () => revalidator.revalidate(),
                            });
                          });
                          setIsMenuOpen(false);
                        }}
                        inputStyleClassName="
                          rounded-full
                          bg-[#E8F7F8]
                          border-[#12AFBD]
                          text-slate-800
                          focus-visible:ring-0
                          focus-visible:border-[#12AFBD]"
                      />
                    </Suspense>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              <Link
                to={`${region === "us" ? `/favorites` : `/${region}/favorites`}`}
                target='_blank'
                className='hidden nav-base:flex'
              >
                <IconButton
                  icon={
                    <Heart
                      size={24}
                      strokeWidth={1}
                      fill={favoritesCount > 0 ? "red" : "none"}
                      color={favoritesCount > 0 ? "red" : "black"}
                    />
                  }
                  label='Favorites'
                  size='md'
                />
              </Link>
              <div className='hidden nav-base:flex gap-4'>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <IconButton
                      icon={<CircleUserRound size={24} strokeWidth={1} />}
                      label='My OfficeHub Login'
                      title='My OfficeHub Login'
                      size='md'
                    />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align='end' className='p-2'>
                    <Suspense fallback={null}>
                      <LoginOptionCard
                        region={region}
                        setAddListingModal={setAddListingModalOpen}
                      />
                    </Suspense>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <div className='hidden nav-base:flex'>
                <DropdownMenu open={open} onOpenChange={setopen}>
                  <DropdownMenuTrigger asChild>
                    <div className='group flex items-center gap-1.5 px-2.5 rounded-full bg-gray-100/80 hover:bg-gray-200/80 transition-all duration-200 cursor-pointer h-[32px] select-none shrink-0 w-fit'>
                      <img
                        src={getFlagUrl(regionConfig?.region)}
                        alt={regionConfig?.country}
                        className='w-6 h-6 object-cover rounded-full shrink-0'
                      />
                      <span className='text-[13px] font-medium text-oh-black whitespace-nowrap'>
                        {displayCurrency?.currency}
                      </span>
                      <ChevronDown
                        size={12}
                        className='text-zinc-400 group-hover:text-zinc-600 transition-all duration-200 group-data-[state=open]:rotate-180 shrink-0'
                        strokeWidth={2}
                      />
                    </div>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align='end'>
                    <DropdownMenuGroup>
                      <DropdownMenuItem
                        className='focus:bg-white p-0'
                        onSelect={(e) => e.preventDefault()}
                      >
                        <Suspense fallback={null}>
                          <GlobalSettings
                            region={region}
                            open={open}
                            setOpen={setopen}
                          />
                        </Suspense>
                      </DropdownMenuItem>
                    </DropdownMenuGroup>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <GlobalPhone isMobile={isMobile} regionConfig={regionConfig} />
              <div className='nav-base:hidden'>
                <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
                  <SheetTrigger asChild>
                    <IconButton
                      icon={<Menu size={30} strokeWidth={2} />}
                      label='Menu'
                      size='lg'
                    />
                  </SheetTrigger>
                  <SheetContent
                    className='p-0 bg-white w-full flex flex-col h-full max-h-[100dvh] overflow-hidden gap-0'
                    style={
                      sheetViewportHeight
                        ? {
                          height: sheetViewportHeight,
                          maxHeight: sheetViewportHeight,
                          bottom: "auto",
                          top: sheetViewportTop,
                        }
                        : undefined
                    }
                  >
                    <SheetHeader className='shrink-0'>
                      <SheetTitle className=' bg-transparent text-oh-black text-left pt-4 px-4 pb-4 border-b border-gray-300 shadow-custom'>
                        <div className='gap-4 flex'>
                          <Link
                            data-spinner='true'
                            to={region === "us" ? "/" : `/${region}`}
                            prefetch='intent'
                            aria-label='Office Hub Home'
                          >
                            <img
                              src={`${process.env.PUBLIC_REMIX_APP_ASSESTS_IMAGES}/assets/office-hub.avif`}
                              alt='Office Hub Logo'
                              title='Office Hub'
                              width={140}
                              height={40}
                              className='w-[140px] h-[40px] object-contain'
                            />
                          </Link>
                        </div>
                      </SheetTitle>
                    </SheetHeader>
                    <div className='flex-1 min-h-0 overflow-hidden flex flex-col'>
                      <Suspense fallback={null}>
                        <Navigation
                          favoritesCount={favoritesCount}
                          navigate={navigate}
                          setAddListingModal={setAddListingModalOpen}
                          addListingModal={addListingModal}
                          isMobile={isMobile}
                          region={region}
                          pathname={pathname}
                          country={regionConfig?.country}
                          keyboardInset={sheetKeyboardInset}
                          ipLocation={ipLocation}
                          setSheetOpen={setIsSheetOpen}
                        />
                      </Suspense>
                    </div>
                  </SheetContent>
                </Sheet>
              </div>
            </div>
            {showAddListingHeaderCta ? (
              <OhButton
                className='hidden md:block'
                onClick={(e) => {
                  e.stopPropagation();
                  openAddListingModal();
                }}
              >
                Add Listing
              </OhButton>
            ) : (
              <Dialog>
                <DialogTrigger asChild>
                  <OhButton
                    className='hidden md:block'
                    onClick={(e) => e.stopPropagation()}
                  >
                    Enquire Now
                  </OhButton>
                </DialogTrigger>
                <DialogContent
                  className='sm:max-w-xl w-full max-h-[85vh] overflow-y-auto'
                  onClick={(e) => e.stopPropagation()}
                >
                  <DialogHeader className='font-semibold border-b pb-4'>
                    Start Your Office Search
                  </DialogHeader>
                  <EnquireNowForm
                    canonicalCountry={regionConfig?.country || DEFAULT_COUNTRY}
                    region={region}
                    path={pathname}
                    className='w-full'
                  />
                </DialogContent>
              </Dialog>
            )}
            {!isMobile && addListingModal ? (
              <Suspense fallback={null}>
                <AddListingTwoStepModal
                  onClose={closeAddListingModal}
                  initialStep={addListingInitialStep}
                  initialEmail={addListingInitialEmail}
                />
              </Suspense>
            ) : null}
          </div>
        </Container>
      </nav>

      {uniqueVisitorModalOpen ? (
        <Suspense fallback={null}>
          <UniqueVisitorModal
            visible
            handleCancel={() => setUniqueVisitorModalOpen(false)}
            handleSubmit={() => setUniqueVisitorModalOpen(false)}
            region={region}
          />
        </Suspense>
      ) : null}
    </>
  );
}